import { useCallback, useMemo } from 'react';
import { 
    ContentEncoder, 
    ContentEncodingValidator, 
    EncodedArticleContent, 
    EncodedSubArticleContent,
    ValidationResult 
} from '../utils/contentEncoding';
import { ArticlePayload } from '../types/articleTypes';

/**
 * Custom React hook for content encoding/decoding operations
 * Provides utilities for secure article content handling
 * 
 * @author JGS Tech Team
 * @since 1.0
 */
export const useContentEncoding = () => {
    
    /**
     * Encodes article payload for secure submission
     */
    const encodeArticlePayload = useCallback((payload: ArticlePayload): ArticlePayload => {
        if (!payload) return payload;
        
        try {
            const encodedPayload = {
                ...payload,
                title: payload.title ? ContentEncoder.encode(payload.title) : payload.title,
                description: payload.description ? ContentEncoder.encode(payload.description) : payload.description,
                body: payload.body ? ContentEncoder.encode(payload.body) : payload.body,
            };
            
            // Encode sub-articles if present
            if (payload.subArticles && payload.subArticles.length > 0) {
                encodedPayload.subArticles = payload.subArticles.map(subArticle => ({
                    ...subArticle,
                    title: subArticle.title ? ContentEncoder.encode(subArticle.title) : subArticle.title,
                    description: subArticle.description ? ContentEncoder.encode(subArticle.description) : subArticle.description,
                    body: subArticle.body ? ContentEncoder.encode(subArticle.body) : subArticle.body,
                }));
            }
            
            return encodedPayload;
            
        } catch (error) {
            console.error('Failed to encode article payload:', error);
            throw new Error(`Content encoding failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }, []);
    
    /**
     * Decodes article payload for display
     */
    const decodeArticlePayload = useCallback((payload: ArticlePayload): ArticlePayload => {
        if (!payload) return payload;
        
        try {
            const decodedPayload = {
                ...payload,
                title: payload.title ? ContentEncoder.decode(payload.title) : payload.title,
                description: payload.description ? ContentEncoder.decode(payload.description) : payload.description,
                body: payload.body ? ContentEncoder.decode(payload.body) : payload.body,
            };
            
            // Decode sub-articles if present
            if (payload.subArticles && payload.subArticles.length > 0) {
                decodedPayload.subArticles = payload.subArticles.map(subArticle => ({
                    ...subArticle,
                    title: subArticle.title ? ContentEncoder.decode(subArticle.title) : subArticle.title,
                    description: subArticle.description ? ContentEncoder.decode(subArticle.description) : subArticle.description,
                    body: subArticle.body ? ContentEncoder.decode(subArticle.body) : subArticle.body,
                }));
            }
            
            return decodedPayload;
            
        } catch (error) {
            console.error('Failed to decode article payload:', error);
            // Return original payload as fallback for backward compatibility
            return payload;
        }
    }, []);
    
    /**
     * Validates article content encoding before submission
     */
    const validateArticleEncoding = useCallback((payload: ArticlePayload): ValidationResult => {
        if (!payload) {
            return { isValid: false, errors: ['Article payload is required'] };
        }
        
        const validationFields: Record<string, string> = {};
        
        if (payload.title) validationFields.title = payload.title;
        if (payload.description) validationFields.description = payload.description;
        if (payload.body) validationFields.body = payload.body;
        
        // Validate main article content
        const mainValidation = ContentEncodingValidator.validateMultipleFields(validationFields);
        const errors = [...mainValidation.errors];
        
        // Validate sub-articles if present
        if (payload.subArticles && payload.subArticles.length > 0) {
            payload.subArticles.forEach((subArticle, index) => {
                const subFields: Record<string, string> = {};
                if (subArticle.title) subFields[`subArticle[${index}].title`] = subArticle.title;
                if (subArticle.description) subFields[`subArticle[${index}].description`] = subArticle.description;
                if (subArticle.body) subFields[`subArticle[${index}].body`] = subArticle.body;
                
                const subValidation = ContentEncodingValidator.validateMultipleFields(subFields);
                errors.push(...subValidation.errors);
            });
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }, []);
    
    /**
     * Encodes individual content field
     */
    const encodeContent = useCallback((content: string): string => {
        return ContentEncoder.encode(content);
    }, []);
    
    /**
     * Decodes individual content field
     */
    const decodeContent = useCallback((encodedContent: string): string => {
        return ContentEncoder.decode(encodedContent);
    }, []);
    
    /**
     * Checks if content is already encoded
     */
    const isContentEncoded = useCallback((content: string): boolean => {
        return ContentEncoder.isEncoded(content);
    }, []);
    
    /**
     * Validates individual content field encoding
     */
    const validateContentEncoding = useCallback((content: string, fieldName: string): ValidationResult => {
        return ContentEncodingValidator.validateForSubmission(content, fieldName);
    }, []);
    
    /**
     * Gets encoding statistics for monitoring
     */
    const getEncodingStats = useCallback((original: string, encoded: string) => {
        return ContentEncoder.getEncodingStats(original, encoded);
    }, []);
    
    /**
     * Batch encode multiple fields
     */
    const encodeMultipleFields = useCallback(<T extends Record<string, string>>(fields: T): T => {
        return ContentEncoder.encodeMultiple(fields);
    }, []);
    
    /**
     * Batch decode multiple fields
     */
    const decodeMultipleFields = useCallback(<T extends Record<string, string>>(fields: T): T => {
        return ContentEncoder.decodeMultiple(fields);
    }, []);
    
    /**
     * Memoized utility functions
     */
    const utils = useMemo(() => ({
        encode: ContentEncoder.encode,
        decode: ContentEncoder.decode,
        isEncoded: ContentEncoder.isEncoded,
        isValidEncoded: ContentEncoder.isValidEncoded,
        encodeMultiple: ContentEncoder.encodeMultiple,
        decodeMultiple: ContentEncoder.decodeMultiple,
        getStats: ContentEncoder.getEncodingStats
    }), []);
    
    /**
     * Form integration helpers
     */
    const formHelpers = useMemo(() => ({
        /**
         * Prepares form data for submission (encodes content)
         */
        prepareForSubmission: (formData: any) => {
            if (!formData) return formData;
            
            const prepared = { ...formData };
            
            // Encode main content fields
            if (prepared.title) prepared.title = ContentEncoder.encode(prepared.title);
            if (prepared.description) prepared.description = ContentEncoder.encode(prepared.description);
            if (prepared.body) prepared.body = ContentEncoder.encode(prepared.body);
            
            return prepared;
        },
        
        /**
         * Prepares data for editing (decodes content)
         */
        prepareForEditing: (data: any) => {
            if (!data) return data;
            
            const prepared = { ...data };
            
            // Decode main content fields
            if (prepared.title) prepared.title = ContentEncoder.decode(prepared.title);
            if (prepared.description) prepared.description = ContentEncoder.decode(prepared.description);
            if (prepared.body) prepared.body = ContentEncoder.decode(prepared.body);
            
            return prepared;
        }
    }), []);
    
    return {
        // Main functions
        encodeArticlePayload,
        decodeArticlePayload,
        validateArticleEncoding,
        
        // Individual field functions
        encodeContent,
        decodeContent,
        isContentEncoded,
        validateContentEncoding,
        
        // Batch operations
        encodeMultipleFields,
        decodeMultipleFields,
        
        // Utilities
        getEncodingStats,
        utils,
        formHelpers,
        
        // Constants
        ENCODING_PREFIX: 'JGS_ENC_V1:'
    };
};

/**
 * Type definitions for the hook
 */
export interface UseContentEncodingReturn {
    encodeArticlePayload: (payload: ArticlePayload) => ArticlePayload;
    decodeArticlePayload: (payload: ArticlePayload) => ArticlePayload;
    validateArticleEncoding: (payload: ArticlePayload) => ValidationResult;
    encodeContent: (content: string) => string;
    decodeContent: (encodedContent: string) => string;
    isContentEncoded: (content: string) => boolean;
    validateContentEncoding: (content: string, fieldName: string) => ValidationResult;
    encodeMultipleFields: <T extends Record<string, string>>(fields: T) => T;
    decodeMultipleFields: <T extends Record<string, string>>(fields: T) => T;
    getEncodingStats: (original: string, encoded: string) => any;
    utils: any;
    formHelpers: any;
    ENCODING_PREFIX: string;
}

export default useContentEncoding;